// ===========================================
// Payment Plans React Query Hooks
// ===========================================
// Custom hooks for payment plan management API operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  adminPaymentPlansApi,
  parentPaymentPlansApi,
  PaymentPlanFilters,
  CreatePaymentPlanInput,
  UpdatePaymentPlanInput,
} from '../api/paymentPlans.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const paymentPlanKeys = {
  all: ['payment-plans'] as const,
  lists: () => [...paymentPlanKeys.all, 'list'] as const,
  list: (filters?: PaymentPlanFilters) => [...paymentPlanKeys.lists(), filters] as const,
  details: () => [...paymentPlanKeys.all, 'detail'] as const,
  detail: (id: string) => [...paymentPlanKeys.details(), id] as const,
  default: () => [...paymentPlanKeys.all, 'default'] as const,
  parent: {
    all: ['parent-payment-plans'] as const,
    list: () => ['parent-payment-plans', 'list'] as const,
    detail: (id: string) => ['parent-payment-plans', 'detail', id] as const,
  },
};

// ===========================================
// ADMIN - PAYMENT PLAN QUERIES
// ===========================================

/**
 * Get all payment plans for the school
 */
export function useAdminPaymentPlans(filters?: PaymentPlanFilters) {
  return useQuery({
    queryKey: paymentPlanKeys.list(filters),
    queryFn: () => adminPaymentPlansApi.getAll(filters),
  });
}

/**
 * Get a single payment plan by ID
 */
export function useAdminPaymentPlan(id: string) {
  return useQuery({
    queryKey: paymentPlanKeys.detail(id),
    queryFn: () => adminPaymentPlansApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Get the default payment plan
 */
export function useDefaultPaymentPlan() {
  return useQuery({
    queryKey: paymentPlanKeys.default(),
    queryFn: () => adminPaymentPlansApi.getDefault(),
  });
}

// ===========================================
// ADMIN - PAYMENT PLAN MUTATIONS
// ===========================================

/**
 * Create a new payment plan
 */
export function useCreatePaymentPlan() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreatePaymentPlanInput) => adminPaymentPlansApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.all });
      enqueueSnackbar('Payment plan created successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to create payment plan', {
        variant: 'error',
      });
    },
  });
}

/**
 * Update a payment plan
 */
export function useUpdatePaymentPlan() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePaymentPlanInput }) =>
      adminPaymentPlansApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.detail(id) });
      enqueueSnackbar('Payment plan updated successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to update payment plan', {
        variant: 'error',
      });
    },
  });
}

/**
 * Delete a payment plan
 */
export function useDeletePaymentPlan() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => adminPaymentPlansApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.all });
      enqueueSnackbar('Payment plan deleted successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to delete payment plan', {
        variant: 'error',
      });
    },
  });
}

/**
 * Set a payment plan as the default
 */
export function useSetDefaultPaymentPlan() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => adminPaymentPlansApi.setDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.all });
      queryClient.invalidateQueries({ queryKey: paymentPlanKeys.default() });
      enqueueSnackbar('Default payment plan updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to set default payment plan', {
        variant: 'error',
      });
    },
  });
}

// ===========================================
// PARENT - PAYMENT PLAN QUERIES
// ===========================================

/**
 * Get active payment plans available to parents
 */
export function useParentPaymentPlans() {
  return useQuery({
    queryKey: paymentPlanKeys.parent.list(),
    queryFn: () => parentPaymentPlansApi.getAll(),
  });
}

/**
 * Get a specific active payment plan
 */
export function useParentPaymentPlan(id: string) {
  return useQuery({
    queryKey: paymentPlanKeys.parent.detail(id),
    queryFn: () => parentPaymentPlansApi.getById(id),
    enabled: !!id,
  });
}

/**
 * Calculate the amount for a payment plan
 */
export function useCalculatePaymentPlan() {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, baseAmount }: { id: string; baseAmount: number }) =>
      parentPaymentPlansApi.calculate(id, baseAmount),
    onError: (error: Error) => {
      enqueueSnackbar(error.message || 'Failed to calculate payment plan', {
        variant: 'error',
      });
    },
  });
}
